/**
 * Organizations API Service
 * Handles organization and clinical center management
 */

import { get, post, put, patch, del } from './client';

// =============================================================================
// Organization Endpoints
// =============================================================================

/**
 * List organizations
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @returns {Promise} Paginated list of organizations
 */
export const listOrganizations = (params = {}) => get('/organizations', params);

/**
 * Get the scope hierarchy: Platform > Region > Organization > Clinical Center > Program.
 *
 * Already scoped by the API to what the caller may see — an organization
 * administrator receives their own organization as the root, a platform
 * administrator receives the whole tree. The response is nested, so each node
 * carries its own `children`.
 *
 * @param {Object} params
 * @param {boolean} params.include_hidden - Include nodes marked hidden
 * @returns {Promise<{items: Array, scoped_to_org: boolean}>}
 */
export const getHierarchy = (params = {}) => get('/organizations/hierarchy', params);

/**
 * Create a level in the hierarchy, plus the detail row its type requires —
 * an `org` also creates the organization record, a `center` the clinical
 * center, a `region` the region with its compliance regime.
 *
 * Platform administrators only; the API returns 403 for anyone else.
 *
 * @param {Object} data
 * @param {string} data.node_type - region | org | center | program | custom label
 * @param {string} data.name
 * @param {string} [data.parent_id] - required for everything below the root
 * @returns {Promise<{id: string, node_type: string, code: string, name: string}>}
 */
export const createHierarchyNode = (data) => post('/organizations/hierarchy/nodes', data);

/**
 * Rename a level, or hide/show it. The name is mirrored onto the underlying
 * organization / center / region record.
 *
 * @param {string} nodeId
 * @param {Object} data - { name?, description?, is_hidden?, is_active? }
 */
export const updateHierarchyNode = (nodeId, data) => patch(`/organizations/hierarchy/nodes/${nodeId}`, data);

/**
 * Full detail for one level: its record, derived counts, and the fields the
 * Basic Info tab shows. Scoped — a node outside the caller's organization 404s.
 */
export const getHierarchyNode = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}`);

/** Everyone granted a role at this level or anywhere beneath it. */
export const getHierarchyNodeUsers = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/users`);

/**
 * Grant a person a role AT this level (the Users tab's "Add user").
 * @param {string} nodeId
 * @param {Object} data - { person_id, role_id, is_primary? }
 */
export const addHierarchyNodeUser = (nodeId, data) => post(`/organizations/hierarchy/nodes/${nodeId}/users`, data);

/** Children enrolled at the centres in this subtree (a program narrows to its own service). */
export const getHierarchyNodeChildren = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/children`);

/**
 * Advanced Settings for a level: its inherit flag and branding, plus the
 * effective branding it actually shows (own, or inherited from an ancestor).
 */
export const getNodeAdvancedSettings = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/advanced-settings`);

/**
 * Save a level's Advanced Settings.
 * @param {string} nodeId
 * @param {Object} data - { inherit_from_parent, branding|null }
 */
export const saveNodeAdvancedSettings = (nodeId, data) => put(`/organizations/hierarchy/nodes/${nodeId}/advanced-settings`, data);

/** Notification templates in a level's scope, optionally filtered by channel ('email'|'sms'). */
export const getNodeNotifications = (nodeId, channel) =>
  get(`/organizations/hierarchy/nodes/${nodeId}/notifications`, channel ? { channel } : {});

/** Create a notification template at a level. data: { channel, name, purpose, subject, body, is_active }. */
export const createNodeNotification = (nodeId, data) => post(`/organizations/hierarchy/nodes/${nodeId}/notifications`, data);

/** Update a template. data: { name, purpose, subject, body, is_active }. */
export const updateNodeNotification = (nodeId, templateId, data) =>
  put(`/organizations/hierarchy/nodes/${nodeId}/notifications/${templateId}`, data);

/** Remove a template (soft delete). */
export const deleteNodeNotification = (nodeId, templateId) => del(`/organizations/hierarchy/nodes/${nodeId}/notifications/${templateId}`);

/** Consent forms that apply at a level (own + inherited), each tagged with scope + editable. */
export const getNodeConsentForms = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/consent-forms`);

/** Create a consent form owned at this level. data: { name, audience }. */
export const createNodeConsentForm = (nodeId, data) => post(`/organizations/hierarchy/nodes/${nodeId}/consent-forms`, data);

/** Update a consent form. data: { name, audience, is_required, status }. */
export const updateNodeConsentForm = (nodeId, formId, data) =>
  put(`/organizations/hierarchy/nodes/${nodeId}/consent-forms/${formId}`, data);

/** Remove a consent form (soft delete). */
export const deleteNodeConsentForm = (nodeId, formId) => del(`/organizations/hierarchy/nodes/${nodeId}/consent-forms/${formId}`);

/** This level's Terms & Conditions ({ enabled, title, content, version }). */
export const getNodeTerms = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/terms`);

/** Save this level's Terms & Conditions. data: { enabled, title, content, version }. */
export const saveNodeTerms = (nodeId, data) => put(`/organizations/hierarchy/nodes/${nodeId}/terms`, data);

/** This level's guided tour config ({ enabled, when_to_show, allow_skip, intro_*, steps[] }). */
export const getNodeGuidedTour = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/guided-tour`);

/** Save this level's guided tour (replaces its steps). */
export const saveNodeGuidedTour = (nodeId, data) => put(`/organizations/hierarchy/nodes/${nodeId}/guided-tour`, data);

/** The caller's organization guided tour, for the tour runner. Never 404s. */
export const getActiveGuidedTour = () => get('/organizations/guided-tour/active');

/**
 * Group sibling levels under a new one — the mockup's "Add new custom level".
 * The new level is inserted between their current parent and the selected
 * nodes, so nothing leaves the subtree it was already in and no access changes.
 *
 * @param {Object} data - { node_ids: string[], name: string, level_type: string }
 */
export const groupHierarchyNodes = (data) => post('/organizations/hierarchy/group', data);

/** Custom level types already in use, for the level-type suggestions. */
export const getHierarchyLevelTypes = () => get('/organizations/hierarchy/level-types');

/**
 * Re-parent levels under another level. Pass `dry_run: true` to get the impact
 * without committing — the API runs the real mutation in a savepoint and rolls
 * it back, so the preview cannot disagree with the commit.
 *
 * @param {Object} data - { node_ids: string[], destination_id: string, dry_run?: boolean }
 */
export const moveHierarchyNodes = (data) => post('/organizations/hierarchy/move', data);

/**
 * Merge sibling levels of the same type into one survivor, which absorbs their
 * children, role grants, clients and staff. `dry_run: true` previews it.
 *
 * @param {Object} data - { node_ids: string[], survivor_id: string, dry_run?: boolean }
 */
export const mergeHierarchyNodes = (data) => post('/organizations/hierarchy/merge', data);

/**
 * The service catalogue a program can be created against (the four packaged
 * program types). Pass a centre's node id to drop the services it already
 * delivers, so the add-program picker only offers something new.
 */
export const getHierarchyServices = (centerNodeId) =>
  get('/organizations/hierarchy/services', centerNodeId ? { center_node_id: centerNodeId } : {});

/**
 * A clinical centre's onboarding checklist and planned date.
 *
 * The checklist template belongs to the ORGANIZATION, so two organizations can
 * run different onboarding; a centre that has never been saved still gets every
 * step back with `is_done` false.
 */
export const getNodeOnboarding = (nodeId, params = {}) => get(`/organizations/hierarchy/nodes/${nodeId}/onboarding`, params);

/**
 * The organization's editable onboarding checklist template.
 *
 * Only meaningful on an organization node — the template is held per
 * organization, so a region / platform roll-up has no single one to edit. The
 * response carries `owner_options`, so the Configure dialog does not hardcode
 * the owner list.
 */
export const getNodeOnboardingTemplate = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/onboarding/template`);

/**
 * Replace the organization's checklist template with an ordered list.
 *
 * @param {string} nodeId - Organization node UUID
 * @param {Object} data - { steps: [{ step_id?, name, owner, needs_previous }] }
 */
export const saveNodeOnboardingTemplate = (nodeId, data) => put(`/organizations/hierarchy/nodes/${nodeId}/onboarding/template`, data);

/** Save the checklist state and planned date. Platform administrators only. */
export const saveNodeOnboarding = (nodeId, data) => put(`/organizations/hierarchy/nodes/${nodeId}/onboarding`, data);

/**
 * Which features are visible at this level, and why.
 *
 * Derived from the organization's product tier and the programs delivered
 * beneath the node — there is nothing to switch on here, which is why there is
 * no matching write.
 */
export const getNodeFeatureVisibility = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/feature-visibility`);

/**
 * The roles that may be granted at this level.
 *
 * Not every role: roles belong to an organization now, so a level inside one
 * offers that organization's roles, and the platform or a region offers only
 * the platform roles.
 */
export const getNodeAssignableRoles = (nodeId) => get(`/organizations/hierarchy/nodes/${nodeId}/assignable-roles`);

/**
 * Add a user at this level, or grant an existing one another role here.
 * An email the platform already knows is adopted — its password is never reset.
 */
export const grantNodeAccess = (nodeId, data) => post(`/organizations/hierarchy/nodes/${nodeId}/users`, data);

/** Revoke one assignment. Only assignments made AT this level can go from here. */
export const revokeNodeAccess = (nodeId, assignmentId) => del(`/organizations/hierarchy/nodes/${nodeId}/users/${assignmentId}`);

/**
 * Get organization by ID
 * @param {string} orgId - Organization UUID
 * @returns {Promise} Organization data
 */
export const getOrganization = (orgId) => get(`/organizations/${orgId}`);

/**
 * Create new organization
 * @param {Object} data - Organization data
 * @param {string} data.name - Organization name
 * @param {string} data.code - Organization code
 * @param {string} data.type - Organization type
 * @returns {Promise} Created organization
 */
export const createOrganization = (data) => post('/organizations', data);

/**
 * Update organization
 * @param {string} orgId - Organization UUID
 * @param {Object} data - Organization data to update
 * @returns {Promise} Updated organization
 */
export const updateOrganization = (orgId, data) => put(`/organizations/${orgId}`, data);

/**
 * Delete organization (soft delete)
 * @param {string} orgId - Organization UUID
 * @returns {Promise} Delete response
 */
export const deleteOrganization = (orgId) => del(`/organizations/${orgId}`);

// =============================================================================
// Organization Settings Endpoints
// =============================================================================

/**
 * Get organization settings
 * @param {string} orgId - Organization UUID
 * @returns {Promise} Organization settings
 */
export const getOrganizationSettings = (orgId) => get(`/organizations/${orgId}/settings`);

/**
 * Update organization settings
 * @param {string} orgId - Organization UUID
 * @param {Object} data - Settings to update
 * @returns {Promise} Updated settings
 */
export const updateOrganizationSettings = (orgId, data) => put(`/organizations/${orgId}/settings`, data);

/**
 * Which MFA methods this organization permits.
 *
 * Returns the whole platform catalogue with a stance on each, plus `has_policy`
 * telling you whether the organization has ever been configured — an
 * unconfigured one comes back with everything enabled, matching how login
 * treats it.
 * @param {string} orgId - Organization UUID
 * @returns {Promise} { has_policy, methods: [{ code, name, description, is_enabled, is_required }] }
 */
/**
 * The branding that applies to the signed-in user, for theming the app.
 *
 * Resolved server-side by walking up the hierarchy from the user's centre (or
 * organization) to the nearest node that owns branding. Returns
 * { source: null, branding: null } when nothing overrides the built-in theme.
 * @returns {Promise} { source, branding: { palette, components, font_family } }
 */
export const getMyBranding = () => get('/organizations/my-branding');

/**
 * Which packaged capabilities the signed-in user's organization is entitled to,
 * from its product tier. Membership is cumulative (Bronze ⊂ Silver ⊂ Gold).
 * @returns {Promise} { is_platform, tier, features: [codes], services, assessments }
 */
export const getMyFeatures = () => get('/organizations/my-features');

export const getOrgMfaMethods = (orgId) => get(`/organizations/${orgId}/mfa-methods`);

/**
 * Replace this organization's MFA policy. While MFA is enabled at least one
 * method must stay enabled; when the master switch is off the methods are kept
 * but no member is challenged at sign-in.
 * @param {string} orgId - Organization UUID
 * @param {Array} methods - [{ code, is_enabled, is_required }]
 * @param {boolean} mfaEnabled - Org-level master switch
 * @returns {Promise} The saved policy
 */
export const updateOrgMfaMethods = (orgId, methods, mfaEnabled = true) =>
  put(`/organizations/${orgId}/mfa-methods`, { methods, mfa_enabled: mfaEnabled });

// =============================================================================
// Clinical Center Endpoints
// =============================================================================

/**
 * List clinical centers
 * @param {Object} params - Query parameters
 * @param {string} params.org_id - Filter by organization
 * @param {string} params.status - Filter by status
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @returns {Promise} Paginated list of clinical centers
 */
export const listClinicalCenters = (params = {}) => get('/organizations/centers', params);

/**
 * Get clinical center by ID
 * @param {string} centerId - Center UUID
 * @returns {Promise} Clinical center data
 */
export const getClinicalCenter = (centerId) => get(`/organizations/centers/${centerId}`);

/**
 * Create new clinical center
 * @param {Object} data - Clinical center data
 * @param {string} data.org_id - Parent organization ID
 * @param {string} data.name - Center name
 * @param {string} data.code - Center code
 * @param {string} data.address - Center address
 * @returns {Promise} Created clinical center
 */
export const createClinicalCenter = (data) => post('/organizations/centers', data);

/**
 * Update clinical center
 * @param {string} centerId - Center UUID
 * @param {Object} data - Center data to update
 * @returns {Promise} Updated clinical center
 */
export const updateClinicalCenter = (centerId, data) => put(`/organizations/centers/${centerId}`, data);

/**
 * Delete clinical center (soft delete)
 * @param {string} centerId - Center UUID
 * @returns {Promise} Delete response
 */
export const deleteClinicalCenter = (centerId) => del(`/organizations/centers/${centerId}`);

/**
 * Get center staff
 * @param {string} centerId - Center UUID
 * @returns {Promise} List of staff at center
 */
export const getCenterStaff = (centerId) => get(`/organizations/centers/${centerId}/staff`);

/**
 * Get center clients
 * @param {string} centerId - Center UUID
 * @returns {Promise} List of clients at center
 */
export const getCenterClients = (centerId) => get(`/organizations/centers/${centerId}/clients`);

export default {
  getNodeAssignableRoles,
  grantNodeAccess,
  revokeNodeAccess,
  getNodeOnboarding,
  saveNodeOnboarding,
  getNodeOnboardingTemplate,
  saveNodeOnboardingTemplate,
  getNodeFeatureVisibility,
  listOrganizations,
  getHierarchy,
  getHierarchyNode,
  getHierarchyNodeUsers,
  addHierarchyNodeUser,
  getHierarchyNodeChildren,
  getNodeAdvancedSettings,
  saveNodeAdvancedSettings,
  getNodeNotifications,
  createNodeNotification,
  updateNodeNotification,
  deleteNodeNotification,
  getNodeConsentForms,
  createNodeConsentForm,
  updateNodeConsentForm,
  deleteNodeConsentForm,
  getNodeTerms,
  saveNodeTerms,
  getNodeGuidedTour,
  saveNodeGuidedTour,
  getActiveGuidedTour,
  groupHierarchyNodes,
  getHierarchyLevelTypes,
  moveHierarchyNodes,
  mergeHierarchyNodes,
  getOrganization,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  getOrganizationSettings,
  updateOrganizationSettings,
  getMyBranding,
  getMyFeatures,
  getOrgMfaMethods,
  updateOrgMfaMethods,
  listClinicalCenters,
  getClinicalCenter,
  createClinicalCenter,
  updateClinicalCenter,
  deleteClinicalCenter,
  getCenterStaff,
  getCenterClients
};
